function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { useInputState } from 'theming/hooks/useInputState';
import { moveFocusOnKeyDown } from 'utilities/dom/focus';
import { getTextWidth } from 'utilities/dom/getTextWidth';
import { createAssignSharedRefs } from 'utilities/input.helpers';
import { Badge } from 'components/Badge/Badge';
import { Button } from 'components/Button/Button';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { CaretDownIcon, InfoIcon } from 'components/Icon';
import { determineComponentState } from 'components/InputBase/InputBase.utils';
import { InputErrorMessage } from 'components/InputErrorMessage/InputErrorMessage';
import { InputLabel } from 'components/InputLabel/InputLabel';
import { Tag } from 'components/Tag/Tag';
import { TooltipArrow, TooltipContent } from 'components/Tooltip/Tooltip.styles';
import { Typography } from 'components/Typography/Typography';
import * as S from './MultiSelect.styles';
const CONTAINER_GAP = 8;
const CONTAINER_PADDING = 16;
const REMAINING_TAGS = 20;
const TAG_DISMISS_WIDTH = 25;
const TAG_PADDING = 12;
const getNextId = (prefix = 'content') => {
  let counter = 0;
  return `${prefix}-${++counter}`;
};
export const MultiSelectOption = /*#__PURE__*/React.forwardRef(({
  name,
  subtitle,
  value,
  disabled,
  onSelect,
  isSelected,
  isBold,
  hasCheckbox = true,
  checkboxState,
  badgeText,
  size = 'large',
  isSelectAllOption = false,
  isBottomAction = false
}, ref) => {
  const handleClick = () => {
    if (!disabled && onSelect) {
      onSelect(value);
    }
  };
  return /*#__PURE__*/React.createElement(S.MultiSelectOption, {
    role: "option",
    ref: ref,
    "aria-checked": isSelected,
    "aria-label": name,
    "aria-disabled": disabled,
    tabIndex: disabled ? undefined : -1,
    onClick: handleClick,
    $size: size,
    $isSelectAllOption: isSelectAllOption,
    $isBottomAction: isBottomAction,
    $disabled: disabled
  }, /*#__PURE__*/React.createElement(S.OptionInnerContainer, null, hasCheckbox ? /*#__PURE__*/React.createElement(Checkbox, {
    id: "",
    ariaLabel: "",
    "aria-hidden": true,
    tabIndex: -1,
    checked: checkboxState ?? isSelected,
    disabled: disabled,
    size: size === 'medium' ? 'small' : 'large'
  }) : null, /*#__PURE__*/React.createElement(S.MultiSelectOptionName, {
    variant: "ui",
    size: size === 'medium' ? 'small' : 'large',
    weight: isBold ? 'bold' : 'regular',
    $disabled: disabled
  }, name, badgeText ? /*#__PURE__*/React.createElement(Badge, {
    text: badgeText,
    size: "small"
  }) : null)), subtitle ? /*#__PURE__*/React.createElement(S.OptionSubtitleContainer, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "ui",
    size: "medium",
    italic: true
  }, subtitle)) : null);
});
const MultiSelect = /*#__PURE__*/React.forwardRef(({
  options,
  placeholder,
  hasError,
  disabled,
  disabledSelectAll = false,
  helperText,
  readOnly,
  optional,
  required,
  label,
  id,
  ariaLabel,
  errorMessage,
  open,
  onOpenChange,
  selectAllText,
  defaultOpen = false,
  onSelectValue,
  onSelectAll,
  onSelectClear,
  onTagDismiss,
  size = 'large',
  displayClearButton = true,
  primarySubtitle,
  secondarySubtitle,
  bottomAction,
  displayRemainingValue = true
}, ref) => {
  const internalRef = useRef(null);
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);

  // This will be here until we update to react 18. Essentially a new unique id is generated per component (not per render)
  // We have useId at home
  const contentId = useMemo(getNextId, []);
  const [internalOptions, setInternalOptions] = useState(options);
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const {
    isFocused,
    isHovered
  } = useInputState(internalRef);
  const handleSelect = value => {
    if (onSelectValue) {
      onSelectValue(value);
      return;
    }
    setInternalOptions(internalOptions => {
      return internalOptions.map(option => {
        if (option.value === value) {
          return {
            ...option,
            isSelected: !option.isSelected,
            shouldRender: !option.isSelected ? true : undefined
          };
        }
        return option;
      });
    });
  };
  const handleSelectAll = () => {
    if (onSelectAll) {
      onSelectAll();
      return;
    }
    setInternalOptions(options => {
      const isAllSelected = options.filter(option => !option.disabled).every(option => option.isSelected);
      return options.map(option => {
        if (option.disabled) {
          return option;
        }
        return {
          ...option,
          isSelected: !isAllSelected,
          shouldRender: !isAllSelected ? true : undefined
        };
      });
    });
  };
  const handleOpenChange = isOpen => {
    if (open === undefined) {
      setInternalOpen(isOpen);
    }
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  };
  const collection = onSelectValue ? options : internalOptions;
  const renderOptions = size => {
    return collection.map(option => {
      if (option.disabled && option.disabledReason) {
        return /*#__PURE__*/React.createElement(RadixTooltip.TooltipProvider, null, /*#__PURE__*/React.createElement(RadixTooltip.Root, {
          key: option.value,
          delayDuration: 0
        }, /*#__PURE__*/React.createElement(RadixTooltip.Trigger, {
          asChild: true
        }, /*#__PURE__*/React.createElement("div", {
          tabIndex: 0
        }, /*#__PURE__*/React.createElement(MultiSelectOption, _extends({}, option, {
          onSelect: handleSelect,
          size: size ?? 'large'
        })))), /*#__PURE__*/React.createElement(RadixTooltip.Portal, null, /*#__PURE__*/React.createElement(TooltipContent, null, option.disabledReason, /*#__PURE__*/React.createElement(TooltipArrow, null)))));
      }
      return /*#__PURE__*/React.createElement(MultiSelectOption, _extends({}, option, {
        key: option.value,
        onSelect: handleSelect,
        size: size ?? 'large'
      }));
    });
  };
  const handleBottomActionClick = value => {
    handleSelect(value);
    handleOpenChange(false);
  };
  const selectAllCheckboxState = useMemo(() => {
    const collection = onSelectValue ? options : internalOptions;
    const hasUnselected = collection.some(({
      isSelected
    }) => !isSelected);
    const hasSelected = collection.some(({
      isSelected
    }) => isSelected);
    if (hasSelected && hasUnselected) {
      return 'indeterminate';
    }
    if (hasSelected && !hasUnselected) {
      return true;
    }
    return false;
  }, [internalOptions, options, onSelectValue]);
  const handleClearSelection = () => {
    if (onSelectClear) {
      onSelectClear();
      return;
    }
    setInternalOptions(internalOptions => internalOptions.map(option => ({
      ...option,
      isSelected: false,
      shouldRender: undefined
    })));
  };

  // Handle responsiveness of Tags
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    setContainerWidth(wrapperRef.current ? wrapperRef.current.clientWidth : 0);
    const updateContainerWidth = container => {
      if (container?.current) {
        setContainerWidth(wrapperRef.current ? wrapperRef.current.clientWidth : 0);
      }
    };
    window.addEventListener('resize', function () {
      updateContainerWidth(wrapperRef);
    });
    return () => window.removeEventListener('resize', function () {
      updateContainerWidth(wrapperRef);
    });
  }, []);
  const filteredSelectedValues = useMemo(() => {
    const currentOptions = onSelectValue ? options : internalOptions;
    if (currentOptions) {
      const filtered = [...currentOptions];
      let totalTagWidth = 0;
      let totalSelectedCount = 0;
      filtered?.forEach((option, i) => {
        if (option.isSelected) {
          option.renderedValue = undefined;
          totalSelectedCount += 1;
          const textWidth = getTextWidth(option.name);
          const additionalWidth = TAG_DISMISS_WIDTH + TAG_PADDING + CONTAINER_GAP;
          totalTagWidth += textWidth + additionalWidth;
          if (totalTagWidth >= containerWidth - CONTAINER_PADDING - REMAINING_TAGS) {
            // if the only selected option is longer than container width, truncate text
            // fixes bug that was not rendering selected item
            if (totalSelectedCount === 1) {
              const width = containerWidth - CONTAINER_PADDING - REMAINING_TAGS;
              option.renderedValue = truncateWithEllipsis(option.name, width);
              option.shouldRender = true;
            } else {
              option.shouldRender = false;
            }
          } else {
            option.shouldRender = true;
          }
        }
      });
      return filtered;
    }
    return currentOptions;
  }, [containerWidth, internalOptions, onSelectValue, options]);
  const renderDisplayValue = useMemo(() => {
    const remainingSelected = filteredSelectedValues?.filter(option => option.shouldRender === false && option.isSelected && !option.disabled).length;
    const selected = filteredSelectedValues?.filter(option => option.shouldRender === true);
    if (selected.length > 0) {
      return /*#__PURE__*/React.createElement(S.MultiSelectOptionContainer, {
        "data-testid": "multi-select-value-container"
      }, selected.map((option, i) => {
        return /*#__PURE__*/React.createElement(Tag, {
          "aria-hidden": true,
          ariaLabel: option.name,
          contentText: option.renderedValue ?? option.name,
          key: option.value,
          dismissable: true,
          readOnly: true,
          value: option.value,
          onDismiss: (value, e) => {
            e?.stopPropagation();
            if (onTagDismiss) {
              onTagDismiss(value);
              return;
            }
            setInternalOptions(internalOptions => internalOptions.map(option => {
              if (option.value === value) {
                return {
                  ...option,
                  isSelected: false,
                  shouldRender: undefined
                };
              }
              return option;
            }));
          },
          size: size === 'medium' ? 'small' : 'medium'
        });
      }), remainingSelected && displayRemainingValue ? /*#__PURE__*/React.createElement(S.RemainingSelected, {
        variant: "ui",
        weight: "bold",
        size: "medium"
      }, "+", remainingSelected) : null);
    }
    return /*#__PURE__*/React.createElement(S.MultiSelectPlaceholder, {
      $size: size
    }, placeholder);
  }, [filteredSelectedValues, onTagDismiss, placeholder, size, displayRemainingValue]);
  const inputState = determineComponentState({
    error: hasError,
    disabled,
    readOnly,
    isFocused,
    isHovered
  });
  const assignSharedRefs = createAssignSharedRefs(ref, internalRef);
  const isInteractive = !(disabled || readOnly);
  const hasErrorMessage = inputState === 'error' && errorMessage;
  return /*#__PURE__*/React.createElement(S.MultiSelectWrapper, {
    $isInteractive: isInteractive,
    ref: wrapperRef
  }, /*#__PURE__*/React.createElement(Popover.Root, {
    open: (open ?? internalOpen) && isInteractive,
    onOpenChange: handleOpenChange
  }, /*#__PURE__*/React.createElement(S.MultiSelectHeader, null, !!label ? /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: id,
    inputState: inputState,
    optional: optional,
    required: required
  }, label) : null, displayClearButton ? /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    size: "medium",
    hasPadding: false,
    onClick: handleClearSelection,
    disabled: !isInteractive
  }, "Clear") : null), /*#__PURE__*/React.createElement(Popover.Trigger, {
    asChild: true
  }, /*#__PURE__*/React.createElement(S.MultiSelectTrigger, {
    role: "combobox",
    ref: assignSharedRefs,
    id: id,
    "aria-expanded": open ?? internalOpen,
    "aria-controls": contentId,
    "aria-label": ariaLabel,
    $state: inputState,
    $isInteractive: isInteractive,
    $size: size
  }, /*#__PURE__*/React.createElement(S.MultiSelectValueContainer, null, renderDisplayValue), /*#__PURE__*/React.createElement(S.MultiSelectChevronContainer, {
    $state: inputState
  }, /*#__PURE__*/React.createElement(CaretDownIcon, null)))), /*#__PURE__*/React.createElement(Popover.Portal, null, /*#__PURE__*/React.createElement(S.MultiSelectContent, {
    id: contentId
  }, /*#__PURE__*/React.createElement(S.ScrollAreaRoot, {
    role: "listbox",
    "aria-multiselectable": true,
    type: "scroll",
    ref: contentRef,
    onKeyDown: e => {
      if (e.key === 'Tab') {
        e.stopPropagation();
        handleOpenChange(false);
        return;
      }
      moveFocusOnKeyDown(e, contentRef.current);
    },
    tabIndex: 1
  }, /*#__PURE__*/React.createElement(S.ScrollAreaViewport, null, /*#__PURE__*/React.createElement(MultiSelectOption, {
    onSelect: handleSelectAll,
    value: "all",
    name: selectAllText,
    checkboxState: selectAllCheckboxState,
    size: size,
    isSelectAllOption: true,
    disabled: collection.every(option => option.disabled) || disabledSelectAll
  }), helperText ? /*#__PURE__*/React.createElement(S.HelperText, null, /*#__PURE__*/React.createElement(InfoIcon, {
    variant: "bold"
  }), " ", helperText) : null, primarySubtitle || secondarySubtitle ? /*#__PURE__*/React.createElement(S.SubtitleContainer, null, primarySubtitle ? /*#__PURE__*/React.createElement(Typography, {
    variant: "ui",
    size: "medium",
    weight: "bold"
  }, primarySubtitle) : null, secondarySubtitle ? /*#__PURE__*/React.createElement(Typography, {
    variant: "ui",
    size: "medium",
    weight: "bold"
  }, secondarySubtitle) : null) : null, renderOptions(size)), /*#__PURE__*/React.createElement(S.Scrollbar, {
    orientation: "vertical"
  }, /*#__PURE__*/React.createElement(S.ScrollbarThumb, null))), bottomAction ? /*#__PURE__*/React.createElement(MultiSelectOption, _extends({}, bottomAction, {
    onSelect: handleBottomActionClick,
    size: size ?? 'large',
    isBottomAction: true
  })) : null))), hasErrorMessage ? /*#__PURE__*/React.createElement(InputErrorMessage, null, errorMessage) : null);
});
export { MultiSelect };
function truncateWithEllipsis(input, containerWidth) {
  let result = '';
  //add for ellipsis '...'
  const dots = '...';
  let currentWidth = getTextWidth(dots);
  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    const inputWidth = getTextWidth(char);
    if (currentWidth + inputWidth > containerWidth) {
      break;
    }
    result += char;
    currentWidth += inputWidth;
  }
  return result.trimEnd() + dots;
}